import React, { useEffect, useState } from 'react';
import Modal from '../Modal';
import Button from '../../ui/buttons/Button';
import Input from '../../ui/inputs/Input';
import CurrencyInput from '../../ui/inputs/CurrencyInput';
import CustomCheckbox from '../../ui/checkboxes/CustomCheckbox';
import Select from '../../ui/selects/Select';
import Textarea from '../../ui/inputs/Textarea';
import { Handshake, X } from 'lucide-react';
import { fetchWithCsrf } from '../../../utils/fetchWithCsrf';
import { snakecaseKeys } from '../../../utils/snakecaseKeys';
import { camelizeKeys } from '../../../utils/camelizeKeys';
import { useToast } from '../../ui/toast/ToastContext';
import PartnerFormList from '../../forms/societaryPartners/PartnerFormList';
import CnpjInput from '../../ui/inputs/CnpjInput';

const SocietaryModal = ({ clientId, societaryId = null, scope, onClose, onSuccess }) => {
  const isEdit = !!societaryId;
  const { addToast } = useToast();

  const [form, setForm] = useState({
    corporateName: '',
    fantasyName: '',
    cnpj: '',
    patrimonialValue: '',
    capital: '',
    societaryPartners: [],
    isPersonal: false,
    participationPercentage: '',
    participationValue: '',
    notes: '',
  });

  // Carrega dados (modo edição)
  useEffect(() => {
    if (isEdit) {
      fetch(`/app/planner/client/${clientId}/dashboard/properties/societaries/${societaryId}?scope=${scope}`)
        .then((res) => res.json())
        .then((data) => {
          setForm(camelizeKeys(data));
        })
        .catch((err) => {
          console.error('Erro ao carregar sociedade', err);
          addToast({ type: 'error', message: 'Erro ao carregar dados do sociedade' });
        });
    }
  }, [isEdit, clientId, societaryId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    // Se o campo alterado for participationPercentage ou patrimonialValue, atualiza participationValue
    if (name === 'participationPercentage') {
      const perc = parseFloat(value);
      const patrimonial = parseFloat(form.patrimonialValue.toString().replace(/[^\d.-]/g, '')) || 0;
      const newValue = isNaN(perc) ? '' : ((perc / 100) * patrimonial).toFixed(2);
      setForm((prev) => ({ ...prev, participationValue: newValue }));
    } else if (name === 'patrimonialValue') {
      const patrimonial = parseFloat(value.toString().replace(/[^\d.-]/g, '')) || 0;
      const perc = parseFloat(form.participationPercentage) || 0;
      const newValue = isNaN(perc) ? '' : ((perc / 100) * patrimonial).toFixed(2);
      setForm((prev) => ({ ...prev, participationValue: newValue }));
    }
  };

  const handleCheckbox = (name) => {
    setForm((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Copia o objeto form, renomeia societaryPartners para societaryPartnersAttributes
    const formToSubmit = {
      ...form,
      societaryPartnersAttributes: form.societaryPartners,
    };

    delete formToSubmit.societaryPartners;

    const payload = snakecaseKeys({ societaryProperty: formToSubmit });

    const url = isEdit
      ? `/app/planner/client/${clientId}/dashboard/properties/societaries/${societaryId}?scope=${scope}`
      : `/app/planner/client/${clientId}/dashboard/properties/societaries?scope=${scope}`;

    const method = isEdit ? 'PATCH' : 'POST';
    const successMsg = isEdit ? 'Sociedade atualizada com sucesso!' : 'Sociedade cadastrada com sucesso!';

    try {
      const res = await fetchWithCsrf(url, {
        method,
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || 'Erro ao salvar sociedade');
      }

      onClose();
      addToast({ type: 'success', message: successMsg });
      onSuccess();
    } catch (err) {
      addToast({ type: 'error', message: err.message });
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="p-10">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3 text-blueDarker font-jost text-2xl font-semibold">
            <Handshake size={24} className="text-tealMedium" strokeWidth={1.25} />
            {isEdit ? 'Editar sociedade' : 'Cadastrar sociedade'}
          </div>
          <button onClick={onClose}>
            <X size={24} className="text-tealBase hover:opacity-80" strokeWidth={1.25} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 font-jost text-sm">
          <div className="col-span-2">
            <Input
              name="corporateName"
              label="Razão social da empresa"
              value={form.corporateName}
              onChange={handleChange}
              required
            />
          </div>
          <Input name="fantasyName" label="Nome fantasia" value={form.fantasyName} onChange={handleChange} />
          <CnpjInput required name="cnpj" label="CNPJ" value={form.cnpj} onChange={handleChange} />
          <CurrencyInput
            name="patrimonialValue"
            label="Valor patrimonial"
            value={form.patrimonialValue}
            onChange={handleChange}
            required
          />
          <CurrencyInput name="capital" label="Capital social" value={form.capital} onChange={handleChange} required />

          <Input
            type="number"
            name="participationPercentage"
            label="Participação (%)"
            value={form.participationPercentage}
            onChange={handleChange}
            required
          />
          <CurrencyInput
            disabled
            name="participationValue"
            label="Participação (R$)"
            value={form.participationValue}
            onChange={handleChange}
            required
          />

          <Textarea
            name="notes"
            label="Observações"
            value={form.notes}
            onChange={handleChange}
            className="col-span-2"
            rows={4}
          />

          <CustomCheckbox
            name="isPersonal"
            label="É um bem particular"
            checked={form.isPersonal}
            onChange={() => handleCheckbox('isPersonal')}
            labelClassName="text-base"
          />

          <div className="col-span-2 flex justify-end items-center mt-1">
            <Button type="submit">{isEdit ? 'SALVAR ALTERAÇÕES' : 'CADASTRAR SOCIEDADE'}</Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default SocietaryModal;
